import React, { useCallback, useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import { Button, Tab, Tabs } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useHistory, useParams } from 'react-router-dom';
import Chip from '@mui/material/Chip';
import { OrganisationResponse } from '../../../types/OrganisationInterfaces';
import { OrganisationService } from '../../../services/OrganisationService';
import LoadingComponent from '../../components/shared/LoadingComponent';
import ErrorComponent from '../../components/shared/ErrorComponent';
import { GeneralHelper } from '../../../helpers/GeneralHelper';

const OrganisationPage = (props: any) => {
  const [tabIndex, setTabIndex] = useState<string>('BASIC');
  const [isLoadingOrganisation, setIsLoadingOrganisation] = useState<boolean>(false);
  const [isOrganisationLoaded, setIsOrganisationLoaded] = useState<boolean>(false);
  const [organisation, setOrganisation] = useState<OrganisationResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const params = useParams<any>();
  const organisationId: number = Number(params.organisationId);

  /*
  const loadOrganisation = useCallback(async (): Promise<void> => {
    setIsLoadingOrganisation(true);
    setIsOrganisationLoaded(false);
    try {
      const response: OrganisationResponse = await OrganisationService.getOrganisation(organisationId);
      setOrganisation(response);
      setIsOrganisationLoaded(true);
    } catch (error) {
      setError(error.message);
    }

    setIsLoadingOrganisation(false);
  }, [setIsLoadingOrganisation, setIsOrganisationLoaded, setOrganisation, setError, organisationId]);

  useEffect(() => {
    if (!isLoadingOrganisation && !isOrganisationLoaded && !error) {
      loadOrganisation();
    }
  }, [isLoadingOrganisation, isOrganisationLoaded, error, loadOrganisation]);


   */
  const history = useHistory();
  const goTo = (location: string) => {
    history.push(location);
  };

  return (
    <Grid container>
      <Grid item lg={12}>
        <Grid container sx={{ mb: 4 }}>
          <Grid item lg={6}>
            <Typography variant={'h4'}>Organisation</Typography>
          </Grid>
          <Grid item lg={6} style={{ textAlign: 'right' }}>
            <Button variant={'outlined'} onClick={() => goTo('/app/organisations')} startIcon={<ArrowBackIcon />}>
              Organisations
            </Button>
          </Grid>
        </Grid>

        {isLoadingOrganisation && <LoadingComponent />}
        {error && <ErrorComponent message={error} />}

        {isOrganisationLoaded && organisation && (
          <Paper>
            <Tabs value={tabIndex} onChange={(event, newValue) => setTabIndex(newValue)}>
              <Tab label="Basic Information" value={'BASIC'} />
              {organisation.solarConsumer && <Tab label="Solar Consumer" value={'CONSUMER'} />}
              {organisation.solarInstaller && <Tab label="Solar Installer" value={'INSTALLER'} />}
            </Tabs>

            {tabIndex === 'BASIC' && (
              <Box sx={{ p: 4 }}>
                <Grid container justifyContent="space-between" sx={{ mb: 3 }}>
                  <Grid item md={6}>
                    <Typography variant={'h6'}>Basic Information</Typography>
                  </Grid>
                  <Grid item>
                    <Button variant={'contained'} onClick={() => goTo(`/app/organisations/${organisationId}/edit`)}>
                      Edit
                    </Button>
                  </Grid>
                </Grid>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item md={3}>
                    <Typography variant={'body2'}>name</Typography>
                    <Typography variant={'subtitle1'}>
                      <strong>{organisation.name}</strong>
                    </Typography>
                  </Grid>
                  <Grid item md={3}>
                    <Typography variant={'body2'}>Roles</Typography>
                    <Typography variant={'subtitle1'}>
                      {organisation.solarConsumer && <Chip label="Solar Consumer" variant={'outlined'} sx={{ mr: 2 }} />}
                      {organisation.solarInstaller && <Chip label="Solar Installer" variant={'outlined'} sx={{ mr: 2 }} />}
                    </Typography>
                  </Grid>
                  <Grid item md={3}>
                    <Typography variant={'body2'}>Website</Typography>
                    <Typography variant={'subtitle1'}>
                      {organisation.websiteUrl ? (
                        <a href={organisation.websiteUrl} target={'_blank'} rel="noreferrer">
                          {organisation.websiteUrl}
                        </a>
                      ) : (
                        '-'
                      )}
                    </Typography>
                  </Grid>
                  <Grid item md={3}>
                    <Typography variant={'body2'}>Address</Typography>
                    <Typography variant={'subtitle1'}>
                      {organisation.address
                        ? `${organisation.address.addressLineOne}, ${organisation.address.addressLineTwo}, ${organisation.address.city}, ${
                            organisation.address.state
                          }, ${organisation.address.postalCode}`
                        : '-'}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            )}

            {tabIndex === 'INSTALLER' && organisation.solarInstaller && (
              <Box sx={{ p: 4 }}>
                <Typography variant={'h6'}>Solar Installer</Typography>
                <Grid container spacing={2} sx={{ mt: 2 }}>
                  <Grid item md={3}>
                    <Typography variant={'body2'}>Accredited at</Typography>
                    <Typography variant={'subtitle1'}>
                      <strong>
                        {organisation.solarInstaller.accreditationDate
                          ? GeneralHelper.formatDate(organisation.solarInstaller.accreditationDate)
                          : '-'}
                      </strong>
                    </Typography>
                  </Grid>
                  <Grid item md={3}>
                    <Typography variant={'body2'}>Accredited by</Typography>
                    <Typography variant={'subtitle1'}>
                      <strong>{organisation.solarInstaller.accreditedByName || '-'}</strong>
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            )}

            {tabIndex === 'CONSUMER' && organisation.solarConsumer && (
              <Box sx={{ p: 4 }}>
                <Typography variant={'h6'}>Solar Consumer</Typography>
                <Grid container spacing={2} sx={{ mt: 2 }}>
                  <Grid item md={6}>
                    <Typography variant={'body2'}>Embedded map</Typography>
                    <iframe
                      height="300"
                      width="100%"
                      title={'Solar Consumer Map'}
                      scrolling="no"
                      src={organisation.solarConsumer.embeddedMap}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}
          </Paper>
        )}
      </Grid>
    </Grid>
  );
};

export default OrganisationPage;
